import "./AdminEditParticipants.css";
import React, { useEffect, useState } from "react";
import { ParticipantAdminEditDisplayResponse } from "../../Interfaces/FundraiserRole";
import { getCurrentlyLoggedInUser } from "../../CommonFunctions";
import SingleParticipantRow from "../../components/EditParticipants/DisplaySingleParticipant";
import config from "../../config";
import fetchWithAuth from "../../services/fetchWithAuth";
import { PrimaryDivision } from "../../components/Forms/JoinFundraiserComponents/types";
import { parseFundraiserID, decodeQueryParameter } from "../../CommonFunctions";
import { useParams } from "react-router/dist";
import axios from "axios";
import UnauthroizedAccount from "../UnauthroizedPages/CoordAndRep";
import { usePopup } from "../../components/Popup/PopupContext";
import PopupModel from "../../components/Popup/PopupModel";

const EditParticipantsAdminPage = () => {
  const [fetchParticipantResponse, setFetchParticipantResponse] =
    useState<ParticipantAdminEditDisplayResponse | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<any | null>(null);
  const [possibleDivisions, setPossibleDivisions] = useState<PrimaryDivision[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showPopup, hidePopup } = usePopup();
  const [participantChanges, setParticipantChanges] = useState<
    Array<Record<string, any>>
  >([]);

  const { fundraiserID } = useParams<{ fundraiserID: string }>();
  let decryptedFundraiserID: number = -1;
  try {
    decryptedFundraiserID = Number(
      decodeQueryParameter(fundraiserID || "").fundraiserID
    );
  } catch {
    decryptedFundraiserID = -1;
  }

  const completedChangesPopup = new PopupModel(
    "Changes Saved!", // header
    (
      <div>The changes you made are now saved!.</div> //body
    ),
    [
      {
        label: "Okay!",
        onClick: () => {
          hidePopup();
        },
      },
    ],
    "40vw" //width
  );

  const errorSecondaryDivision = new PopupModel(
    "Changes NOT Saved!", // header
    (
      <div>
        Error: When changing the primary division, the secondary division must
        also change — even if it has the same name. (For example, switching from
        “Major Dodgers” to “Minor Dodgers” still requires you to reselect the
        secondary division.)
      </div> //body
    ),
    [
      {
        label: "Okay!",
        onClick: () => {
          hidePopup();
        },
      },
    ],
    "40vw" //width
  );
  const submitParticipantChanges = async () => {
    const hasInvalidSecondaryDivision = participantChanges.some(
      (item) => item.secondary_division_id === -1
    );

    if (hasInvalidSecondaryDivision) {
      showPopup(
        errorSecondaryDivision.header,
        errorSecondaryDivision.body,
        errorSecondaryDivision.buttons,
        errorSecondaryDivision.width
      );
      return;
    }
    setLoading(true);

    try {
      const response = await fetchWithAuth(
        `${config.API_BASE_URL}fundraiser/${decryptedFundraiserID}/participants/admin-view/edit/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(participantChanges),
        }
      );
      if (response.status === 200 || response.status === 201) {
        fetchFundraiserParticipants();
        showPopup(
          completedChangesPopup.header,
          completedChangesPopup.body,
          completedChangesPopup.buttons,
          completedChangesPopup.width
        );

        setParticipantChanges([]);
      }
    } catch {
      console.log("Failed to submit participant changes");
    } finally {
      setLoading(false);
    }
  };

  const fetchFundraiserParticipants = async () => {
    try {
      const response = await fetchWithAuth(
        `${config.API_BASE_URL}fundraiser/${decryptedFundraiserID}/participants/admin-view/`
      );
      const data: ParticipantAdminEditDisplayResponse = await response.json();
      setFetchParticipantResponse(data);
    } catch (error) {
      console.log(`Failed to fetch participants ${error}`);
    }
  };

  const updateParticipantChanges = (
    participant_id: number,
    updatedData: Record<string, any>
  ) => {
    setParticipantChanges((prevChanges) => {
      const index = prevChanges.findIndex(
        (p) => p.participant_id === participant_id
      );

      if (index !== -1) {
        // If the participant exists, update their data
        const updatedChanges = [...prevChanges];
        updatedChanges[index] = { ...updatedChanges[index], ...updatedData };
        return updatedChanges;
      } else {
        // If the participant doesn't exist, add a new entry with the participant_id
        return [...prevChanges, { participant_id, ...updatedData }];
      }
    });
  };

  useEffect(() => {
    const fetchLoggedInUser = async () => {
      try {
        const user = await getCurrentlyLoggedInUser();
        console.log(user);
        setLoggedInUser(user);
      } catch (error) {}
    };

    const fetchPossibleDivisions = async () => {
      try {
        const response = await fetchWithAuth(
          `${config.API_BASE_URL}division/fundraiser/${decryptedFundraiserID}/`
        );
        const data: PrimaryDivision[] = await response.json();
        setPossibleDivisions(data);
      } catch (error) {
        console.log(`Failed to fetch divisions ${error}`);
      }
    };

    fetchPossibleDivisions();
    fetchLoggedInUser();
    fetchFundraiserParticipants();
    document.title = "Fundraiser Participant Edit";
  }, []);

  if (
    loggedInUser &&
    fetchParticipantResponse &&
    loggedInUser.email !== fetchParticipantResponse.coordinator_email &&
    loggedInUser.email !== fetchParticipantResponse.rep_email
  ) {
    return <UnauthroizedAccount />;
  }
  return (
    <div className="admin-edit-participants-container">
      <div className="admin-edit-participants-table-container">
        <table className="admin-edit-participants-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Primary Division</th>
              <th>Secondary Division</th>
              <th>Has Bought Out</th>
              <th>Profit Raised</th>
              <th>Contacts Inputted</th>
            </tr>
          </thead>
          <tbody>
            {fetchParticipantResponse?.participants.map((participant, i) => (
              <SingleParticipantRow
                key={i}
                participant={participant}
                possibleDivisions={possibleDivisions}
                updateParticipantChanges={updateParticipantChanges}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-edit-participants-changes-view">
        <h2>Changes</h2>
        <button
          onClick={submitParticipantChanges}
          disabled={participantChanges.length === 0}
          style={{
            backgroundColor: "blue",
            color: "white",
            padding: "8px 16px",
            border: "none",
            borderRadius: "4px",
          }}
        >
          Submit Changes
        </button>
        {participantChanges.map((change, i) => {
          const participant = fetchParticipantResponse?.participants.find(
            (p) => p.participant_id === change.participant_id
          );

          const primaryDivChange = change["primary_division_id"];
          const secondaryDivChange = change["secondary_division_id"];

          // Check if profile_image_url is present in the object
          const hasProfileImageKey = "profile_image_url" in change;
          const profileImageRemoved =
            hasProfileImageKey && change.profile_image_url === null;

          const primaryDivId =
            primaryDivChange ?? participant?.primary_division?.id;
          const primaryDivision = possibleDivisions.find(
            (d) => d.id === primaryDivId
          );
          const secondaryDivision = possibleDivisions
            .flatMap((d) => d.secondary_divisions) // Extract all secondary divisions
            .find((s) => s.id === secondaryDivChange);

          return (
            <div key={i}>
              <p>
                <b>{participant ? participant.full_name : "Unknown"}</b>
              </p>
              {primaryDivChange && (
                <p>
                  Primary Division changed to{" "}
                  {primaryDivision?.division_name ?? "Not Found"}
                </p>
              )}
              {secondaryDivChange && (
                <p>
                  Secondary Division changed to{" "}
                  {secondaryDivision?.division_name ?? "Not Found"}
                </p>
              )}
              {profileImageRemoved && <p>Profile image was removed</p>}
            </div>
          );
        })}
      </div>
      {/* Loading Popup */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      <style>{`
      /* Loading Spinner Styles */
      .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 999;
        color: white;
      }

      .loading-spinner {
        border: 8px solid rgba(255, 255, 255, 0.3);
        border-top: 8px solid white;
        border-radius: 50%;
        width: 60px;
        height: 60px;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div>
  );
};

export default EditParticipantsAdminPage;
